/* eslint-disable max-len */
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import { dateToString } from '@/utils/helpers';
import loaderMixin from '@/mixins/loader';
import processingWithdrawalCancelTransaction from './components/processing-transaction/processing-transaction.vue';

const TYPE_CHECK = 1;
const TYPE_TRANSFER = 2;
const TYPE_COINBASE = 4;
const TYPE_BANK_TRANSFER = 5;

const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY hh:mm A';
const TITLE_DATE_FORMAT = 'ddd MMM Do, YYYY';
const EMPTY_DISPLAY = '-';

const hasValue = value => value !== null && typeof value !== 'undefined' && value !== '';
const hasNumber = value => hasValue(value) && !Number.isNaN(Number(value));
const formatCurrency = (value) => {
  if (!hasNumber(value)) return EMPTY_DISPLAY;

  const amount = Number(value);
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const WITHDRAW_CANCEL_STEPS = {
  INITIAL: 1,
  PROCESSING: 2,
  FINISHED_SUCCESS: 3,
  FINISHED_FAILED: 4,
};

export default {
  mixins: [
    loaderMixin,
  ],
  props: {
    request: {
      type: Object,
      required: true,
    },
    isDeposit: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    processingWithdrawalCancelTransaction,
  },
  data() {
    return {
      hasFailed: false,
      WITHDRAW_CANCEL_STEPS,
      currentStep: WITHDRAW_CANCEL_STEPS.INITIAL,
      currentStepData: {},
    };
  },
  watch: {
    withdrawalCancelStep(value) {
      if (this.currentStep === WITHDRAW_CANCEL_STEPS.PROCESSING && value.step === WITHDRAW_CANCEL_STEPS.FINISHED_SUCCESS) {
        this.setSuccessSideNotification('Your withdrawal has been cancelled.');
        this.$emit('complete');
      }
      if (this.currentStep === WITHDRAW_CANCEL_STEPS.PROCESSING && value.step === WITHDRAW_CANCEL_STEPS.FINISHED_FAILED) {
        this.setErrorSideNotification('Unfortunately, we could not process your request.');
        this.$emit('complete');
      }
    },
  },
  computed: {
    ...mapState('wallet', ['withdrawalCancelStep']),
    isTransfer() {
      return this.request.type === TYPE_TRANSFER;
    },
    isCheck() {
      return this.request.type === TYPE_CHECK;
    },
    isCoinbase() {
      return this.request.type === TYPE_COINBASE;
    },
    isBankTransfer() {
      return this.request.type === TYPE_BANK_TRANSFER;
    },
    requestDateValue() {
      if (!this.request) return null;

      return this.request.date || this.request.dateCreated || null;
    },
    requestTypeLabel() {
      if (!this.request) return 'Pending Withdrawal';

      if (this.request.typeName && this.request.typeName !== 'Withdrawal') {
        return this.request.typeName;
      }

      if (this.paymentMethod !== EMPTY_DISPLAY) {
        return `${this.paymentMethod} Withdrawal`;
      }

      return 'Pending Withdrawal';
    },
    requestTitle() {
      if (!this.requestDateValue || !moment(this.requestDateValue).isValid()) {
        return this.requestTypeLabel;
      }

      return `${this.requestTypeLabel} on ${moment(this.requestDateValue).format(TITLE_DATE_FORMAT)}`;
    },
    paymentMethod() {
      if (!this.request) return EMPTY_DISPLAY;

      switch (this.request.type) {
        case TYPE_TRANSFER:
          return 'ACH';
        case TYPE_CHECK:
          return 'Check';
        case TYPE_COINBASE:
          return 'USD Coin';
        case TYPE_BANK_TRANSFER:
          return 'Bank Transfer';
        default:
          return EMPTY_DISPLAY;
      }
    },
    formattedAmount() {
      return formatCurrency(this.request && this.request.amount);
    },
    formattedFee() {
      return `(${formatCurrency(this.request && this.request.fee)})`;
    },
    formattedNet() {
      return formatCurrency(this.request && this.request.net);
    },
    formattedDate() {
      if (!this.requestDateValue || !moment(this.requestDateValue).isValid()) return EMPTY_DISPLAY;

      return dateToString(this.requestDateValue, DATE_DISPLAY_FORMAT);
    },
    confirmationNumber() {
      return this.request && this.request.confirmationNumber ? this.request.confirmationNumber : EMPTY_DISPLAY;
    },
    detailRows() {
      if (!this.request) return [];

      if (this.isTransfer) {
        return [
          {
            label: 'Account Type',
            value: this.request.accountType || EMPTY_DISPLAY,
          },
          {
            label: 'Routing Number',
            value: this.request.routingNumber || EMPTY_DISPLAY,
          },
          {
            label: 'Account Number',
            value: hasValue(this.request.accountNumberLastFour) ? `********${this.request.accountNumberLastFour}` : EMPTY_DISPLAY,
          },
        ];
      }

      if (this.isCheck) {
        const locationParts = [this.request.city, this.request.stateCode].filter(hasValue);
        const location = locationParts.join(', ');
        const cityLineParts = [location, this.request.postalCode].filter(hasValue);
        const lines = [
          this.request.payeeName,
          this.request.address,
          cityLineParts.join(' '),
        ].filter(hasValue);

        if (hasValue(this.request.countryCode)) {
          lines.push(this.request.countryCode);
        }

        return [{
          label: 'Check Payable To',
          lines: lines.length > 0 ? lines : [EMPTY_DISPLAY],
          isStacked: true,
        }];
      }

      if (this.isCoinbase) {
        return [{
          label: 'Recipient',
          value: hasValue(this.request.email) ? this.request.email : EMPTY_DISPLAY,
        }];
      }

      return [];
    },
  },
  methods: {
    ...mapActions('wallet', ['cancelWithdrawal']),
    ...mapActions('notifications', ['setErrorSideNotification', 'setSuccessSideNotification']),
    isStep(name) {
      return this.currentStep === name;
    },
    onCancel() {
      const payload = {
        withdrawalId: this.request.withdrawalId,
      };
      // this.currentStep = WITHDRAW_CANCEL_STEPS.PROCESSING;
      this.loaderAdd();
      this.cancelWithdrawal(payload)
        .then(() => {
          this.loaderRemove();
          this.setSuccessSideNotification('Your withdrawal has been cancelled.');
          this.$emit('complete');
        }).catch(() => {
          this.loaderRemove();
          this.setErrorSideNotification('Unfortunately, we could not process your request.');
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/wallet/pending-requests/pending-request-withdrawal/pending-request-withdrawal.js